import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { isPlatformBrowser } from '@angular/common';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';
import { SpinnerService } from '../../../../shared/services/spinner.service';
import { Observable } from 'rxjs';

/**
 * Bar Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-bar-graph',
  standalone: false,
  templateUrl: './bar-graph.component.html',
  styleUrl: './bar-graph.component.scss'
})
export class BarGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private barGraphReportSubscription: any;
  private isBarViewInitialized = false;
  private isChartCreating = false;

  @Input() reportData: any = null;

  constructor(
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _commonService: CommonService,
    private _spinnerService: SpinnerService
  ) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.barGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Bar Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isBarViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isBarViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  private ensureChartDisposal(): void {
    if (this.root) {
      try {
        this._baseChartService.disposeChart(this.root);
        this.root = null as any;
      } catch (error) {
        console.warn('Error disposing chart:', error);
      }
    }
  }

  private checkDOMElement(): boolean {
    const chartContainer = document.getElementById('bar-chartdiv');
    if (!chartContainer) {
      console.error('Bar Chart - Chart container not found! Make sure element with id "bar-chartdiv" exists');
      return false;
    }
    return true;
  }

  private validateAndCleanData(data: BarChartData[]): BarChartData[] {
    if (!data || data.length === 0) {
      console.warn('No data provided to validateAndCleanData');
      return [];
    }

    console.log('Validating and cleaning data:', data);

    const cleanedData = data.map((item, index) => {
      const cleanedItem: any = { ...item };
      
      // Ensure all string values are properly formatted
      Object.keys(cleanedItem).forEach(key => {
        const value = cleanedItem[key];
        if (typeof value === 'string') {
          // Clean up string values
          cleanedItem[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Handle object values - try to extract meaningful string
          if (value.col_value !== undefined) {
            cleanedItem[key] = String(value.col_value).trim();
          } else if (value.drilldown_object && value.col_value !== undefined) {
            cleanedItem[key] = String(value.col_value).trim();
          } else {
            // Fallback to JSON string
            cleanedItem[key] = JSON.stringify(value);
          }
        }
      });

      console.log(`Cleaned item ${index}:`, cleanedItem);
      return cleanedItem;
    });

    console.log('Final cleaned data:', cleanedData);
    return cleanedData;
  }

  createChart(data: BarChartData[]) {
    // Prevent multiple simultaneous chart creations
    if (this.isChartCreating) {
      console.log('Chart creation already in progress, skipping...');
      return;
    }

    this.isChartCreating = true;

    // Ensure proper disposal of previous chart
    this.ensureChartDisposal();

    // Check if the chart container exists
    if (!this.checkDOMElement()) {
      this.isChartCreating = false;
      return;
    }

    // Clean and validate the data
    const cleanedData = this.validateAndCleanData(data);
    if (cleanedData.length === 0) {
      console.warn('No valid data after cleaning');
      this.isChartCreating = false;
      return;
    }

    // Add a small delay to ensure DOM is ready
    setTimeout(() => {
      /* try {
        // Double-check DOM element exists
        if (!this.checkDOMElement()) {
          this.isChartCreating = false;
          return;
        }


        this.root = this._baseChartService.createRoot('bar-chartdiv');
        this._baseChartService.attachExporting(this.root, { filePrefix: 'bar', data: cleanedData });

        const chart = this._baseChartService.createChartContainer(this.root, {
          panX: false,
          panY: false,
          wheelX: 'none',
          wheelY: 'none',
          layout: this.root.verticalLayout
        });
        this._baseChartService.setupColorPalette(chart);

        const categoryField = this._baseChartService.getCategoryField(cleanedData);
        const valueFields = this._baseChartService.getValueFields(cleanedData);

       

        // Create axes - For horizontal bar chart, category is Y-axis, value is X-axis
        const categoryAxis = chart.yAxes.push(
          am5xy.CategoryAxis.new(this.root, {
            categoryField: categoryField,
            renderer: am5xy.AxisRendererY.new(this.root, {
              minGridDistance: 30,
              cellStartLocation: 0.1,
              cellEndLocation: 0.9
            })
          })
        );

        // Configure category axis labels for Y-axis (horizontal bar chart)
        categoryAxis.get("renderer").labels.template.setAll({
          rotation: 0,
          centerY: am5.p50,
          centerX: am5.p100,
          maxWidth: 200,
          maxChars: 30,
          breakWords: true,
          oversizedBehavior: 'truncate',
          fontSize: 12,
          fill: am5.color(0x000000)
        });

        // Additional configuration for category axis
        categoryAxis.set('maxZoomFactor', 1);

        const valueAxis = chart.xAxes.push(
          am5xy.ValueAxis.new(this.root, {
            renderer: am5xy.AxisRendererX.new(this.root, {})
          })
        );

        // Set data
        categoryAxis.data.setAll(cleanedData);
        
       
        // Get the color set from the chart
        const colorSet = chart.get('colors') as am5.ColorSet;
        let colorIndex = 0;

        // Create series for each value field
        valueFields.forEach((valueField) => {
          let series = chart.series.push(
            am5xy.ColumnSeries.new(this.root, {
              name: valueField,
              xAxis: valueAxis,
              yAxis: categoryAxis,
              valueXField: valueField,
              categoryYField: categoryField
            })
          );

          // Assign a different color to each series
          if (colorSet) {
            const color = colorSet.next();
            series.columns.template.set('fill', color);
            console.log(`Assigned color to series: ${valueField}`);
          }

          // Set enhanced tooltip with value and label display
          const tooltip = this._baseChartService.createStandardTooltip(this.root, 'bar', {
            showValue: true,
            showCategory: true,
            showSeries: true
          });
          series.set('tooltip', tooltip);

          // Set data
          series.data.setAll(cleanedData);
        });

        // Add cursor
        chart.set('cursor', am5xy.XYCursor.new(this.root, {
          xAxis: valueAxis
        }));

        // Add legend
        let legend = chart.children.push(
          this._baseChartService.createLegend(this.root)
        );

        // Configure legend
        legend.set('maxHeight', 40);

        // Configure legend markers
        let markerTemplate = legend.markers.template;
        markerTemplate.set('width', 12);
        markerTemplate.set('height', 12);

        // Set legend data
        legend.data.setAll(chart.series.values);

        // Force chart to update and render
        setTimeout(() => {
          chart.root.container.set("layout", chart.root.container.get("layout"));
          console.log('Chart rendered with categories:', categoryAxis.data.values);
          
          // Additional debugging for category labels
          const categoryLabels = categoryAxis.get("renderer").labels;
          console.log('Category labels template:', categoryLabels.template);
        }, 100);

       

       

        

      } catch (error) {
        console.error('Error creating bar chart:', error);
        // Hide spinner on error
        this.isChartCreating = false;
      } */

      
 
        try {
          if (!this.checkDOMElement()) {
            this.isChartCreating = false;
            return;
          }
         
          this.root = this._baseChartService.createRoot('bar-chartdiv');
          // Enable export menu (PNG, JPG, SVG, PDF, CSV/JSON)
          this._baseChartService.attachExporting(this.root, { 
            filePrefix: 'bar',
            data: cleanedData
          });

          const chart = this._baseChartService.createChartContainer(this.root, {
            panX: false,
            panY: false,
            wheelX: 'none',
            wheelY: 'none',
            layout: this.root.verticalLayout
          });
         
          this._baseChartService.setupColorPalette(chart);
         
          const categoryField = this._baseChartService.getCategoryField(cleanedData);
          const valueField = this._baseChartService.getValueFields(cleanedData)[0]; // Use only one
         
          const categoryAxis = chart.yAxes.push(
            am5xy.CategoryAxis.new(this.root, {
              categoryField: categoryField,
              renderer: am5xy.AxisRendererY.new(this.root, {
                minGridDistance: 1,
                cellStartLocation: 0.1,
                cellEndLocation: 0.9,
                inversed: false
              })
            })
          );
         
          categoryAxis.get("renderer").labels.template.setAll({
            rotation: 0,
            centerY: am5.p50,
            centerX: am5.p100,
            maxWidth: 200,
            breakWords: true,
            fontSize: 12,
            fill: am5.color(0x000000),
            oversizedBehavior: "none", // show full label
            visible: true
          });
         
          categoryAxis.set('maxZoomFactor', 1);
          categoryAxis.data.setAll(cleanedData);
         
          const valueAxis = chart.xAxes.push(
            am5xy.ValueAxis.new(this.root, {
              renderer: am5xy.AxisRendererX.new(this.root, {})
            })
          );
         
          const colorSet = am5.ColorSet.new(this.root, {});
         
          const series = chart.series.push(
            am5xy.ColumnSeries.new(this.root, {
              name: valueField,
              xAxis: valueAxis,
              yAxis: categoryAxis,
              valueXField: valueField,
              categoryYField: categoryField,
            })
          );
         
          // Apply different color per bar
          // Set tooltip on column template
          series.columns.template.setAll({
            tooltipText: "{categoryY}: {valueX}",
            tooltipY: am5.p50,
            tooltipX: am5.p50,
            fillOpacity: 1
          });
          
          // Apply different color per bar using data index
          series.columns.template.adapters.add("fill", (fill, target) => {
            const dataContext = target.dataItem?.dataContext;
            const index = cleanedData.indexOf(dataContext as BarChartData);
            if (index !== -1) {
              return colorSet.getIndex(index);
            }
            return fill;
          });
          
          series.columns.template.adapters.add("stroke", (stroke, target) => {
            const dataContext = target.dataItem?.dataContext;
            const index = cleanedData.indexOf(dataContext as BarChartData);
            if (index !== -1) {
              return colorSet.getIndex(index);
            }
            return stroke;
          });
        
         
          // Add value label on each bar
          series.bullets.push(() =>
            am5.Bullet.new(this.root, {
              locationX: 1,
              sprite: am5.Label.new(this.root, {
                text: "{valueX}",
                populateText: true,
                fill: am5.color(0x000000),
                centerY: am5.p50,
                centerX: am5.p0,
                fontSize: 12,
                dx: 5
              })
            })
          );
         
          series.data.setAll(cleanedData);
         
          chart.set('cursor', am5xy.XYCursor.new(this.root, {
            xAxis: valueAxis
          }));

          // Add legend (one entry per category/bar)
          const legend = chart.children.push(
            this._baseChartService.createLegend(this.root)
          );

          this._baseChartService.setupLegendAnimation(legend);

          legend.markers.template.setAll({ width: 12, height: 12 });
          legend.labels.template.setAll({
            text: '{categoryY}',
            fontSize: 12
          });
          legend.valueLabels.template.set('forceHidden', true);

          const getLegendColor = (target: any) => {
            const dataItem = target.dataItem;
            if (dataItem) {
              const index = series.dataItems.indexOf(dataItem);
              if (index >= 0) {
                return colorSet.getIndex(index);
              }
            }
            return undefined;
          };

          legend.markerRectangles.template.adapters.add('fill', (fill, target) => {
            return getLegendColor(target) ?? fill;
          });

          legend.markerRectangles.template.adapters.add('stroke', (stroke, target) => {
            return getLegendColor(target) ?? stroke;
          });

          const updateLegend = () => {
            if (series.dataItems.length > 0) {
              legend.data.setAll(series.dataItems);
            }
          };

          series.events.on('datavalidated', updateLegend);
          updateLegend();
         
          setTimeout(() => {
            chart.root.container.set("layout", chart.root.container.get("layout"));
            console.log('Chart rendered with categories:', categoryAxis.data.values);
            // Reset the flag after chart is created
            this.isChartCreating = false;
          }, 100);
        } catch (error) {
          console.error('Error creating bar chart:', error);
          this.isChartCreating = false;
        }



    }, 100);
  }

  private updateChart(data: BarChartData[]) {
    // Update the stored data
    this.data = data;

    // Ensure proper disposal before creating new chart
    this.ensureChartDisposal();
    
    // Add a small delay to ensure proper disposal
    setTimeout(() => {
      this.createChart(data);
      
      // Update the chart data in the registry
      BaseChartService.updateActiveChartData(data);
    }, 150);
  }

  ngOnDestroy(): void {
    // Ensure proper disposal
    this.ensureChartDisposal();
    
    if (this.barGraphReportSubscription) {
      this.barGraphReportSubscription.unsubscribe();
    }
  }
}
